import { Link } from 'react-router-dom';
import type { VisitListItem } from '@/lib/stores/visit-store';
import { StatusBadge } from '@/components/ui/StatusBadge';

interface VisitCardProps {
  visit: VisitListItem;
}

/**
 * Format milliseconds into a human-readable duration.
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return '< 1s';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

/**
 * Format a timestamp into a relative time string.
 */
function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

/**
 * Status dot color mapping with optional ring and pulse.
 */
function getStatusDotClasses(status: string, isRecording: boolean): string {
  if (isRecording) {
    return 'bg-red-500 ring-4 ring-red-500/20 recording-pulse';
  }
  switch (status) {
    case 'draft':
      return 'bg-slate-400 ring-4 ring-slate-400/15';
    case 'finalizing':
      return 'bg-amber-500 ring-4 ring-amber-500/20';
    case 'closed':
      return 'bg-slate-500 ring-4 ring-slate-500/15';
    default:
      return 'bg-slate-400 ring-4 ring-slate-400/15';
  }
}

/**
 * Visit list card -- premium light mode with .card-interactive hover-lift,
 * status dot with colored ring, subtle metric pills, and clean typography.
 */
export function VisitCard({ visit }: VisitCardProps): JSX.Element {
  return (
    <Link
      to={`/visits/${visit.visitId}`}
      className="card-interactive group relative block animate-fade-in overflow-hidden"
    >
      {/* Recording shimmer line at top */}
      {visit.isRecording && (
        <div className="absolute inset-x-0 top-0 h-[2px] shimmer" />
      )}

      <div className="relative p-5">
        {/* Top row: avatar + patient info + status cluster */}
        <div className="flex items-start justify-between gap-4">
          {/* Left: avatar with status dot + patient info */}
          <div className="flex items-start gap-3.5 min-w-0">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl text-sm font-semibold transition-all duration-300 ${
                  visit.isRecording
                    ? 'border border-red-200 bg-red-50 text-red-600 shadow-sm shadow-red-100/60'
                    : 'border border-teal-100 bg-teal-50 text-teal-700'
                }`}
              >
                {visit.patientCode.slice(0, 2).toUpperCase()}
              </div>
              {/* Status dot with colored ring */}
              <span
                className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ${getStatusDotClasses(visit.status, visit.isRecording)}`}
              />
            </div>

            {/* Name + ID */}
            <div className="min-w-0">
              <p className="truncate font-medium text-slate-800 transition-colors duration-200 group-hover:text-teal-700">
                {visit.patientName ?? visit.patientCode}
              </p>
              <p className="mt-0.5 font-mono text-2xs text-slate-400 tracking-wide">
                {visit.visitId}
              </p>
            </div>
          </div>

          {/* Right: status badge + timestamp */}
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <StatusBadge status={visit.status} kind="visit" />
            <span className="font-mono text-2xs tabular-nums text-slate-400">
              {formatRelativeTime(visit.updatedAt)}
            </span>
          </div>
        </div>

        {/* Gradient divider */}
        <div className="divider-gradient mt-4 mb-3" />

        {/* Bottom row: metric pills + consent badge */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {/* Duration pill */}
          {visit.totalRecordingMs > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 border border-slate-100">
              <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium tabular-nums text-slate-600">{formatDuration(visit.totalRecordingMs)}</span>
            </span>
          )}

          {/* Word count pill */}
          {visit.wordCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 border border-slate-100">
              <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
              </svg>
              <span className="font-medium tabular-nums text-slate-600">{visit.wordCount.toLocaleString()}</span>
            </span>
          )}

          {/* Segment count pill */}
          {visit.segmentCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 border border-slate-100">
              <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
              </svg>
              <span className="font-medium tabular-nums text-slate-600">{visit.segmentCount}</span>
            </span>
          )}

          {/* Consent badge pushed to right */}
          <span className="ml-auto">
            <StatusBadge status={visit.consentState} kind="consent" size="sm" />
          </span>
        </div>
      </div>
    </Link>
  );
}
